/* eslint-disable @typescript-eslint/naming-convention */
import { Grid, Alert, LinearProgress } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FormControlImage, FormControlInput } from '../../components-molecules/FormControl';
import { FormPageComponent } from '../../components-molecules/FormPageComponent';
import { ModalCardComponent } from '../../components-molecules/Modal';
import { SectionHeader } from '../../components-molecules/section/SectionHeader';
import { IStyles } from '../../constants/theme';
import { environment } from '../../environment';
import { FILE_VALIDATION_WITH_TYPE, REQUIRED_VALIDATION } from '../../forms/_predefinedValidations';
import { useTypedSelector } from '../../hooks/TypedReduxHooks';
import { UploadToAzure } from '../../hooks/uploadToAzure';
import { EditIndustryInsightRequest } from '../../service/serviceService';
import { AssetStatusButtonGroup } from '../AssetStatusButtonGroup';

interface EditInsightModel {
  pdf: File | null;
  image: File | null;
  title: string;
  description?: string;
  published: boolean;
}

interface IOption {
  title: string;
  color: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  value: boolean;
}

interface onProgressResponse {
  fileName: string;
  percentage: number;
}

export function EditInsight(): JSX.Element {
  const buttonGroupOptions: IOption[] = [
    { title: 'Published', color: 'success', value: true },
    { title: 'Unpublished', color: 'error', value: false }
  ];
  const token = useTypedSelector(state => state.userReducer.token);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [scrollToTop, setScrollToTop] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<onProgressResponse[]>([]);
  const [percentage, setPercentage] = useState(0);
  const navigate = useNavigate();

  const insight = useTypedSelector(state => state.industryInsightReducer.currentData);
  const [defaultInsight] = useState<any>({
    pdf: insight.PdfUrl,
    image: insight.ImageUrl,
    title: insight.Title,
    description: insight.Description,
    published: insight.Published
  });
  const { control, handleSubmit, formState: { isValid }, setValue, getValues } = useForm<any>({ mode: 'all', reValidateMode: 'onChange', defaultValues: defaultInsight });

  useEffect(() => {
    setValue('published', defaultInsight.published, { shouldDirty: true, shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculatePercentage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProgress]);

  const submit: SubmitHandler<EditInsightModel> = async (data) => {
    if (token) {
      setLoading(true);
      setScrollToTop(true);

      let insightImageFileName;
      let imageUrl; 
      if (typeof data.image === 'object') {
        insightImageFileName = await UploadToAzure({
          data: [data.image],
          onProgress: onProgress,
          folderName: environment.insightFolder
        });
        imageUrl = insightImageFileName[0].Url; 
      } else {
        const url: string = data?.image;
        const urlArray = url.split('/');
        imageUrl = urlArray[urlArray.length - 1];
      }

      let insightPdfFileName;
      let pdfUrl;
      if (typeof data.pdf === 'object') {
        insightPdfFileName = await UploadToAzure({
          data: [data.pdf],
          onProgress: onProgress,
          folderName: environment.insightFolder
        });
        pdfUrl = insightPdfFileName[0].Url; 
      } else {
        const url: string = data?.pdf;
        const urlArray = url.split('/');
        pdfUrl = urlArray[urlArray.length - 1];
      }

      EditIndustryInsightRequest(
        token,
        {
          Id: insight.Id,
          PdfUrl: pdfUrl,
          ImageUrl: imageUrl,
          Title: data.title,
          Description: data.description,
          Published: data.published
        },
        () => {
          setLoading(false);
          navigate(-1);
        },
        (error) => {
          setErrorMessage(error.response?.data.Error || '');
          setLoading(false);
          setScrollToTop(false);
        });
    }
  };

  const onProgress = (data: onProgressResponse) => {
    const result = loadingProgress?.find(x => data.fileName === x.fileName);
    let tempLoadingProcesses = loadingProgress;

    if (result !== undefined) {
      tempLoadingProcesses= tempLoadingProcesses?.map(x => {
        if (x.fileName === data.fileName) {
          return {...x, percentage: data.percentage};
        } else {
          return x;
        }
      });
    } else {
      tempLoadingProcesses?.push(data);
    }
    setLoadingProgress(tempLoadingProcesses);
  };

  const calculatePercentage = () => {
    let total = 0;
    loadingProgress.forEach(x => {
      total += x.percentage / loadingProgress.length;
    });
    setPercentage(Math.round(total));
  };
  
  return (
    <FormPageComponent
      hasError={!!errorMessage}
      title="Edit Insight"
      buttonTitle="Submit"
      buttonDisabled={!isValid}
      close={() => navigate(-1)}
      submit={handleSubmit(submit)}
      scrollToTop={scrollToTop}
    >
      {loading
        ? <LinearProgress variant="determinate" color="secondary" value={percentage}  sx={{ zIndex: 2 }} />
        : <></>
      }
      <Fragment>
        <Grid item sx={{ flexGrow: 1 }}>
          {!!errorMessage
            && <Alert severity="error">{errorMessage}</Alert>
          }
        </Grid>
        <ModalCardComponent>
          <SectionHeader title="Insight details" />
          <Fragment>
            <Grid item container columnSpacing={4} sx={{ flexGrow: 1 }}>
              <Grid item xs={12}>
                <FormControlImage
                  accept=".pdf"
                  data-testid="edit-insight-pdf"
                  initialValue={insight.PdfUrl}
                  initialFileType='pdf'
                  control={control}
                  field={{
                    label: 'PDF File',
                    name: 'pdf',
                    inputType: 'logo',
                    validation: {
                      required: REQUIRED_VALIDATION,
                      validate: FILE_VALIDATION_WITH_TYPE(50, '.pdf')
                    }
                  }}
                  title="PDF File"
                  subtitle="Maximum file size 50 MB."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlImage
                  accept="image/*"
                  data-testid="edit-insight-image"
                  control={control}
                  initialValue={insight.ThumbnailUrl || insight.ImageUrl}
                  initialFileType='image'
                  field={{
                    label: 'Preview image',
                    name: 'image',
                    inputType: 'logo',
                    validation: {
                      required: REQUIRED_VALIDATION,
                      validate: FILE_VALIDATION_WITH_TYPE(5, 'image/*')
                    }
                  }}
                  title="Preview Image"
                  subtitle="JPG file exported from the original PDF works best. Maximum file size 5 MB."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlInput
                  data-testid="add-insight-title"
                  control={control}
                  field={{
                    label: 'Title',
                    name: 'title',
                    inputType: 'text',
                    validation: {
                      required: REQUIRED_VALIDATION,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlInput
                  data-testid="add-insight-description"
                  control={control}
                  field={{
                    label: 'Description',
                    name: 'description',
                    inputType: 'text',
                    validation: {
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ margin: '32px 0 0' }}>
                <AssetStatusButtonGroup
                  subtitle="Published insights are visible to customers and unpublished insights are hidden."
                  title="Insight Status"
                  data-testid="add-insight-published"
                  value={getValues('published')}
                  onChange={(value: boolean) => setValue('published', value, { shouldDirty: true, shouldValidate: true })}
                  options={buttonGroupOptions}
                />
              </Grid>
            </Grid>
          </Fragment>
        </ModalCardComponent>
        {loading ? (
          <div style={styles.overlay}>
          </div>
        ) : (
          <></>
        )}
      </Fragment>
    </FormPageComponent>
  );
}

const styles: IStyles={
  overlay: {
    position: 'fixed',
    padding: 0,
    margin: 0,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.5)',
    zIndex: 1
  }
};
